import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "camp_name", label: "Camp Name", placeholder: "Mini Makers Market Camp" },
  { key: "tagline", label: "Tagline / Slogan", placeholder: "Create it. Brand it. Showcase it." },
  { key: "hero_headline", label: "Hero Headline (main page H1)", placeholder: "Where kids ages 4–16 create real spa products…", textarea: true, full: true },
  { key: "hero_subheadline", label: "Hero Subheadline", placeholder: "3 days of hands-on making…", textarea: true, full: true },
  { key: "dates_label", label: "Dates Label", placeholder: "e.g. August 13–15, 2026" },
  { key: "time_label", label: "Daily Time Label", placeholder: "e.g. 10:00 AM – 4:00 PM" },
  { key: "workshop_days_label", label: "Workshop Days Label (hero)", placeholder: "Aug 13–14 · 10AM–4PM" },
  { key: "showcase_label", label: "Showcase Day Label", placeholder: "Showcase Day · August 15 · 1:00 PM – Finish", full: true },
  { key: "duration_label", label: "Duration Label", placeholder: "e.g. 3 days · Includes Final Showcase" },
  { key: "location_label", label: "Location / Venue Label", placeholder: "e.g. 11560 149 St NW, Edmonton, AB", full: true },
  { key: "address_line", label: "Address Line (shown in hero map card)", placeholder: "11560 149 St NW, Edmonton, AB", full: true },
  { key: "age_range_label", label: "Age Range Label", placeholder: "Ages 4–16" },
  { key: "spots_left_label", label: "Scarcity Label", placeholder: "Limited spots available" },
  { key: "early_bird_ends_label", label: "Early Bird Ends Label", placeholder: "Until June 30, 2026" },
  { key: "whatsapp_phone", label: "WhatsApp Number (digits only)", placeholder: "17808632906" },
];

const NUMERIC_FIELDS = [
  { key: "price_early_bird", label: "Price · Early Bird ($)" },
  { key: "price_regular", label: "Price · Regular ($)" },
  { key: "sibling_discount_2", label: "Discount · 2 children (%)" },
  { key: "sibling_discount_3", label: "Discount · 3 children (%)" },
  { key: "sibling_discount_4", label: "Discount · 4 children (%)" },
];

// Hero spots counter — editable per group
const SPOTS_FIELDS = [
  { key: "spots_banner_text", label: "Banner Text Override (top line)", placeholder: "Leave blank for auto: \"Live · 44 spots left across all groups\"", full: true },
  { key: "display_total_remaining", label: "Total Spots Left Override", placeholder: "Leave blank to auto-compute", numeric: true },
  { key: "capacity_4_7", label: "Capacity · Ages 4–7", numeric: true },
  { key: "capacity_8_11", label: "Capacity · Ages 8–11", numeric: true },
  { key: "capacity_12_16", label: "Capacity · Ages 12–16", numeric: true },
  { key: "display_remaining_4_7", label: "\"Left\" Override · Little Makers", placeholder: "Blank = auto", numeric: true },
  { key: "display_remaining_8_11", label: "\"Left\" Override · Junior Makers", placeholder: "Blank = auto", numeric: true },
  { key: "display_remaining_12_16", label: "\"Left\" Override · Senior Makers", placeholder: "Blank = auto", numeric: true },
  { key: "group_title_4_7", label: "Group Title · Ages 4–7", placeholder: "Little Makers" },
  { key: "group_title_8_11", label: "Group Title · Ages 8–11", placeholder: "Junior Makers" },
  { key: "group_title_12_16", label: "Group Title · Ages 12–16", placeholder: "Senior Makers" },
  { key: "group_age_label_4_7", label: "Age Label · Group 1", placeholder: "Ages 4–7" },
  { key: "group_age_label_8_11", label: "Age Label · Group 2", placeholder: "Ages 8–11" },
  { key: "group_age_label_12_16", label: "Age Label · Group 3", placeholder: "Ages 12–16" },
];

const AdminMarketCampSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/market-camp-settings`);
      setSettings(res.data);
    } catch {
      toast.error("Failed to load settings");
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const update = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/market-camp-settings`, settings);
      toast.success("Settings saved successfully");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <AdminLayout><div className="text-center py-12 text-[#A1A1AA]">Loading…</div></AdminLayout>;
  }

  return (
    <AdminLayout>
    <div className="text-[#F5F5F0]" data-testid="admin-camp-settings-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-black mt-1">Market Camp Settings</h1>
          <p className="text-[#A1A1AA] text-sm">Edit dates, prices, and copy displayed on the public landing page.</p>
        </div>

        <div className="bg-[#0A0A0A] rounded-2xl border border-[rgba(212,175,55,0.15)] p-5 sm:p-6 space-y-5">
          <SectionTitle>Camp Information</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(f => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">{f.label}</label>
                {f.textarea ? (
                  <textarea
                    value={settings[f.key] || ""}
                    onChange={e => update(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={2}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm resize-none"
                    data-testid={`field-${f.key}`}
                  />
                ) : (
                  <input
                    type="text"
                    value={settings[f.key] || ""}
                    onChange={e => update(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                    data-testid={`field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>

          <SectionTitle>Pricing &amp; Discounts</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            {NUMERIC_FIELDS.map(f => (
              <div key={f.key}>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">{f.label}</label>
                <input
                  type="number" min="0" step="any"
                  value={settings[f.key] ?? ""}
                  onChange={e => update(f.key, parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                  data-testid={`field-${f.key}`}
                />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-3 p-3 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] cursor-pointer">
            <input
              type="checkbox" checked={!!settings.early_bird_active}
              onChange={e => update("early_bird_active", e.target.checked)}
              className="w-5 h-5 rounded" style={{ accentColor: "#D4AF37" }}
              data-testid="field-early-bird-active"
            />
            <div>
              <p className="font-bold text-[#F5F5F0] text-sm">Early bird pricing active</p>
              <p className="text-xs text-[#A1A1AA]">When ON, the landing page shows the early bird price. Turn off after the early bird window ends.</p>
            </div>
          </label>

          <SectionTitle>Hero Spots Counter (Live)</SectionTitle>
          <p className="text-[11px] text-[#A1A1AA] -mt-3">
            Override the displayed numbers on the live hero counter. Leave override fields blank to auto-compute from capacity − paid registrations.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {SPOTS_FIELDS.map(f => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">{f.label}</label>
                <input
                  type={f.numeric ? "number" : "text"}
                  min={f.numeric ? "0" : undefined}
                  value={settings[f.key] ?? ""}
                  onChange={e => update(f.key, f.numeric ? (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 0)) : e.target.value)}
                  placeholder={f.placeholder || ""}
                  className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                  data-testid={`field-${f.key}`}
                />
              </div>
            ))}
          </div>

          <SectionTitle>Social Proof Ticker</SectionTitle>
          <p className="text-[11px] text-[#A1A1AA] -mt-3">
            Shows anonymized booking toasts like &ldquo;Sarah J. from Edmonton just registered 2 kids · 5 min ago&rdquo; on the public page.
            By default this pulls from real recent registrations. To use manual messages instead, fill the list below (one per line).
          </p>
          <label className="flex items-center gap-3 p-3 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] cursor-pointer">
            <input
              type="checkbox" checked={settings.social_proof_enabled !== false}
              onChange={e => update("social_proof_enabled", e.target.checked)}
              className="w-5 h-5 rounded" style={{ accentColor: "#D4AF37" }}
              data-testid="field-social-proof-enabled"
            />
            <div>
              <p className="font-bold text-[#F5F5F0] text-sm">Social proof ticker enabled</p>
              <p className="text-xs text-[#A1A1AA]">Cycles recent bookings in the bottom-left of the public landing page.</p>
            </div>
          </label>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">
              Rotation Speed (milliseconds — higher = slower)
            </label>
            <input
              type="number" min="2000" step="500"
              value={settings.social_proof_rotation_ms ?? 11000}
              onChange={e => update("social_proof_rotation_ms", parseInt(e.target.value, 10) || 11000)}
              placeholder="11000 (= 11 seconds)"
              className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
              data-testid="field-social-proof-rotation"
            />
            <p className="text-[10px] text-[#52525B] mt-1">Suggested: 8000-15000. Higher number = each name stays on screen longer.</p>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">
              Manual Messages (optional · one per line · overrides auto)
            </label>
            <textarea
              value={(Array.isArray(settings.social_proof_messages) ? settings.social_proof_messages : []).join("\n")}
              onChange={e => {
                const lines = e.target.value.split("\n").map(s => s.trim()).filter(Boolean);
                update("social_proof_messages", lines);
              }}
              placeholder={"Sarah J. from Edmonton just registered 2 kids\nMike T. from Sherwood Park just secured Senior Makers spots\nLeave blank to auto-show real recent bookings"}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm font-mono resize-none"
              data-testid="field-social-proof-messages"
            />
          </div>

          <SectionTitle id="refer-a-friend">Refer-a-Friend</SectionTitle>
          <p className="text-[11px] text-[#A1A1AA] -mt-3">
            When a friend uses a parent&apos;s share code at paid checkout, a credit appears in
            <strong className="text-[#F5F5F0]"> Camp Referrals ($)</strong>. You then pay it out manually
            (e-Transfer, cash, refund, future credit) and mark as paid.
          </p>
          <label className="flex items-center gap-3 p-3 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] cursor-pointer">
            <input
              type="checkbox" checked={settings.referral_enabled !== false}
              onChange={e => update("referral_enabled", e.target.checked)}
              className="w-5 h-5 rounded" style={{ accentColor: "#D4AF37" }}
              data-testid="field-referral-enabled"
            />
            <div>
              <p className="font-bold text-[#F5F5F0] text-sm">Refer-a-Friend enabled</p>
              <p className="text-xs text-[#A1A1AA]">Show the &ldquo;Got a referral code?&rdquo; input on registration and the share panel on the success page.</p>
            </div>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">Credit Amount per Referral ($) <span className="text-[10px] text-[#A1A1AA] font-normal lowercase">(default)</span></label>
              <input
                type="number" min="0" step="any"
                value={settings.referral_credit_amount ?? 20}
                onChange={e => update("referral_credit_amount", parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                data-testid="field-referral-credit-amount"
              />
              <p className="text-[10px] text-[#52525B] mt-1">Used for parent share codes. Ambassadors can override per-code.</p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">Currency</label>
              <select
                value={settings.referral_credit_currency || "cad"}
                onChange={e => update("referral_credit_currency", e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                data-testid="field-referral-currency"
              >
                <option value="cad">CAD</option>
                <option value="usd">USD</option>
                <option value="eur">EUR</option>
                <option value="gbp">GBP</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">Default Payout Method</label>
              <select
                value={settings.referral_payout_default_method || "etransfer"}
                onChange={e => update("referral_payout_default_method", e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                data-testid="field-referral-payout-method"
              >
                <option value="etransfer">Interac e-Transfer</option>
                <option value="cash">Cash at event</option>
                <option value="refund">Stripe partial refund</option>
                <option value="credit">Future-event credit</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">Voucher Email Subject</label>
              <input
                type="text"
                value={settings.referral_voucher_email_subject || ""}
                onChange={e => update("referral_voucher_email_subject", e.target.value)}
                placeholder="🎉 You've earned a $20 thank-you voucher · Mini Makers Market Camp"
                className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm"
                data-testid="field-referral-email-subject"
              />
              <p className="text-[10px] text-[#52525B] mt-1">Leave blank for default. Use {"{amount}"} for the $ value.</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-1.5">
              Share Text Template (use {"{code}"} and {"{url}"})
            </label>
            <textarea
              rows={3}
              value={settings.referral_share_text_template || ""}
              onChange={e => update("referral_share_text_template", e.target.value)}
              placeholder="Hey! My kid is going to Mini Makers Market Camp. Use my code {code} when you register — I get $20 thank-you credit. Sign up: {url}"
              className="w-full px-3 py-2.5 rounded-lg bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] focus:border-[#D4AF37] outline-none text-sm resize-none"
              data-testid="field-referral-share-text"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={fetchSettings} className="px-4 py-2.5 rounded-lg border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] inline-flex items-center gap-1.5" data-testid="reset-btn">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#D4AF37] text-[#050505] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="save-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

const SectionTitle = ({ children, id }) => (
  <h2 id={id} className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold pt-2 border-t border-[rgba(212,175,55,0.1)] first:border-0 first:pt-0 scroll-mt-20">{children}</h2>
);

export default AdminMarketCampSettings;
